/**
 * Combined Tracking Hook
 * Provides unified access to both Google Analytics and PostHog tracking
 */

import { useCallback } from 'react';
import {
  trackButtonClickGA,
  trackTemplateUsageGA
} from '@/lib/analytics';
import {
  trackButtonClickPostHog,
  trackTemplateUsagePostHog,
  trackConversionPostHog,
  trackSocialClickPostHog
} from '@/lib/posthogAnalytics';
import { usePostHog } from '@/components/analytics/PostHogProvider';

interface TrackingOptions {
  templateType?: string;
  countryCode?: string;
  province?: string;
  region?: string;
  category?: string;
  label?: string;
}

/**
 * Combined tracking hook for templates
 * Provides unified interface for GA and PostHog tracking
 */
export const useCombinedTracking = () => {
  const { isInitialized: isPostHogReady } = usePostHog();

  // Template usage tracking
  const trackTemplateUsage = useCallback((
    templateName: string,
    region: string,
    features: string[],
    options: TrackingOptions = {}
  ) => {
    // Google Analytics tracking
    trackTemplateUsageGA(templateName, region, features);

    // PostHog tracking
    if (isPostHogReady) {
      trackTemplateUsagePostHog(templateName, region, features);
    }

    if (import.meta.env.DEV) {
    }
  }, [isPostHogReady]);

  // Button click tracking
  const trackButtonClick = useCallback((
    buttonText: string,
    buttonId: string,
    buttonType: string,
    service?: string,
    countryCode?: string,
    templateType?: string,
    options: TrackingOptions = {}
  ) => {
    // Google Analytics tracking
    trackButtonClickGA(buttonText, buttonId, buttonType, service, countryCode, templateType);

    // PostHog tracking
    if (isPostHogReady) {
      trackButtonClickPostHog(buttonText, buttonId, buttonType, service, countryCode, templateType);

      // Track conversions for payment-related buttons
      if (buttonType.includes('modal_open') || buttonType.includes('payment')) {
        trackConversionPostHog(buttonType.includes('modal_open') ? 'modal_open' : 'payment_click', service || 'general');
      }
    }

    if (import.meta.env.DEV) {
    }
  }, [isPostHogReady]);

  // Social media click tracking
  const trackSocialClick = useCallback((
    platform: string,
    linkUrl: string,
    options: TrackingOptions = {}
  ) => {
    // Google Analytics tracking (if implemented)
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'social_click', {
        event_category: 'Social Media',
        event_label: platform,
        link_url: linkUrl,
        ...options
      });
    }

    // PostHog tracking
    if (isPostHogReady) {
      trackSocialClickPostHog(platform, linkUrl);
    }

    if (import.meta.env.DEV) {
    }
  }, [isPostHogReady]);

  // Conversion tracking
  const trackConversion = useCallback((
    conversionType: string,
    service: string,
    value?: number,
    currency?: string,
    options: TrackingOptions = {}
  ) => {
    // PostHog conversion tracking
    if (isPostHogReady) {
      trackConversionPostHog(conversionType, service, value, currency);
    }

    // Google Analytics conversion tracking
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'conversion', {
        event_category: 'Conversion',
        event_label: `${conversionType} - ${service}`,
        value: value,
        currency: currency,
        ...options
      });
    }

    if (import.meta.env.DEV) {
    }
  }, [isPostHogReady]);

  // Enhanced button click with automatic context detection
  const trackEnhancedButtonClick = useCallback((
    buttonElement: HTMLElement,
    buttonType: string,
    service?: string,
    additionalProps: Record<string, any> = {}
  ) => {
    if (!buttonElement) return;

    const buttonText = buttonElement.textContent?.trim() || buttonElement.getAttribute('aria-label') || 'Unknown';
    const buttonId = buttonElement.id || buttonElement.getAttribute('data-tracking-id') || `btn-${Date.now()}`;

    // Detect context automatically
    const countryCode = localStorage.getItem('test_country') ||
                       (typeof window !== 'undefined' ? navigator.language.split('-')[1]?.toUpperCase() : undefined);
    const region = localStorage.getItem('user_region') || 'LATAM';

    trackButtonClick(buttonText, buttonId, buttonType, service, countryCode, 'AutoDetected', {
      region,
      page_url: window.location.href,
      user_agent: navigator.userAgent,
      ...additionalProps
    });
  }, [trackButtonClick]);

  return {
    // Core tracking functions
    trackTemplateUsage,
    trackButtonClick,
    trackSocialClick,
    trackConversion,

    // Enhanced tracking
    trackEnhancedButtonClick,

    // Status
    isPostHogReady,
    isTrackingReady: true, // GA is always ready

    // Debug info
    debugInfo: {
      posthogReady: isPostHogReady,
      region: localStorage.getItem('user_region') || 'LATAM',
      testCountry: localStorage.getItem('test_country'),
      userAgent: typeof window !== 'undefined' ? navigator.userAgent : 'SSR'
    }
  };
};

/**
 * Hook for template-specific tracking
 * Automatically includes template context
 */
export const useTemplateTracking = (templateName: string) => {
  const combinedTracking = useCombinedTracking();

  const trackTemplateLoad = useCallback((region: string, features: string[]) => {
    combinedTracking.trackTemplateUsage(templateName, region, features, {
      templateType: templateName
    });
  }, [combinedTracking, templateName]);

  const trackTemplateButton = useCallback((
    buttonText: string,
    buttonId: string,
    buttonType: string,
    service?: string
  ) => {
    combinedTracking.trackButtonClick(buttonText, buttonId, buttonType, service, undefined, templateName);
  }, [combinedTracking, templateName]);

  return {
    ...combinedTracking,
    trackTemplateLoad,
    trackTemplateButton,
    templateName
  };
};

export default useCombinedTracking;